import { useCallback, useEffect, useRef, KeyboardEvent } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyIconButton,
  Icon,
  Text,
} from '@equip.health/ui';

import { MaiSendMeta, MaiSendTrigger } from '~/analytics/maiChatEvents';

import { MAI_CHAT_DISCLAIMER } from './maiChat.constants';
import { MaiPromptIcon, MaiSuggestedPrompt } from './maiChat.types';
import { useMaiChatDrafts } from './MaiChatDraftsContext';

// Optional values type the lookup as a miss, so a token the catalog adds ahead
// of this client renders its chip bare.
const PROMPT_ICON_GLYPHS: Record<MaiPromptIcon, FAIconName | undefined> = {
  assessment: FAIconName.clipboardListRegular,
  celebration: FAIconName.badgeCheckRegular,
  opportunity: FAIconName.bullseye,
  summary: FAIconName.fileLinesRegular,
};

const TEXTAREA_MAX_HEIGHT = 140;

interface MaiChatComposerProps {
  onSend: (text: string, meta: MaiSendMeta) => void;
  patientFirstName: string;
  patientId: string;
  suggestedPrompts: ReadonlyArray<MaiSuggestedPrompt>;
  isDisabled?: boolean;
}

const MaiChatComposer = ({
  onSend,
  patientFirstName,
  patientId,
  suggestedPrompts,
  isDisabled = false,
}: MaiChatComposerProps) => {
  // Held above the router so navigating tabs — which unmounts this panel —
  // doesn't discard an unsent message.
  const { drafts, setDraft } = useMaiChatDrafts();
  const inputValue = drafts[patientId] ?? '';
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const focusTextarea = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    focusTextarea();
    // Focusing a textarea that already holds a restored draft can leave the
    // caret at offset 0, so put it after the text the clinician had typed.
    const textarea = textareaRef.current;
    textarea?.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [focusTextarea]);

  const submitTextarea = useCallback(
    (trigger: MaiSendTrigger) => {
      const trimmed = inputValue.trim();
      if (!trimmed || isDisabled) return;
      onSend(trimmed, { trigger });
      setDraft(patientId, '');
      focusTextarea();
    },
    [focusTextarea, inputValue, isDisabled, onSend, patientId, setDraft],
  );

  const submitChip = useCallback(
    (prompt: MaiSuggestedPrompt) => {
      if (isDisabled) return;
      onSend(prompt.text, {
        trigger: 'Suggested Prompt',
        promptId: prompt.id,
      });
      focusTextarea();
    },
    [focusTextarea, isDisabled, onSend],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitTextarea('Enter Key');
      }
    },
    [submitTextarea],
  );

  const isEmptyInput = !inputValue.trim();

  return (
    <ChakraBox
      data-testid="mai-chat-composer"
      padding="12px 14px 14px"
      background="neutral.background.primary"
    >
      <ChakraFlex
        direction="column"
        border="1px solid"
        borderColor="neutral.line"
        borderRadius="14px"
        background="neutral.background.primary"
        overflow="hidden"
        transition="border-color 150ms, box-shadow 150ms"
        _focusWithin={{
          borderColor: 'primary.100',
          boxShadow: '0 0 0 3px rgba(0, 127, 121, 0.08)',
        }}
      >
        {suggestedPrompts.length > 0 && (
          <ChakraFlex
            data-testid="mai-chat-composer__chips"
            alignItems="center"
            gap="6px"
            flexWrap="wrap"
            padding="8px 10px"
            borderBottom="1px solid"
            borderColor="neutral.line"
          >
            <Text
              color="neutral.tertiary"
              paddingLeft="2px"
              textTransform="uppercase"
              variant="overlineMedium"
            >
              Try
            </Text>
            {suggestedPrompts.map((prompt) => {
              const glyph = PROMPT_ICON_GLYPHS[prompt.icon];
              return (
                <ChakraFlex
                  as="button"
                  type="button"
                  key={prompt.id}
                  id={`mai-chat-composer__chip-${prompt.id}`}
                  data-testid={`mai-chat-composer__chip-${prompt.id}`}
                  disabled={isDisabled}
                  onClick={() => submitChip(prompt)}
                  alignItems="center"
                  gap="6px"
                  padding="6px 10px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor="neutral.line"
                  background="neutral.background.secondary"
                  color="neutral.secondary"
                  fontSize="12px"
                  fontWeight="500"
                  lineHeight="1"
                  whiteSpace="nowrap"
                  cursor="pointer"
                  transition="150ms"
                  _hover={{
                    background: 'primary.10',
                    borderColor: 'primary.50',
                    color: 'primary.100',
                  }}
                  _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  {glyph && <Icon name={glyph} color="primary.100" size="sm" />}
                  {prompt.label}
                </ChakraFlex>
              );
            })}
          </ChakraFlex>
        )}
        <ChakraFlex direction="column" padding="8px 10px 6px">
          <ChakraBox
            as="textarea"
            ref={textareaRef}
            data-testid="mai-chat-composer__textarea"
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDraft(patientId, e.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder={`Ask about ${patientFirstName}'s care...`}
            aria-label={`Ask about ${patientFirstName}'s care`}
            rows={1}
            readOnly={isDisabled}
            border="0"
            outline="0"
            resize="none"
            minHeight="22px"
            maxHeight={`${TEXTAREA_MAX_HEIGHT}px`}
            sx={{ fieldSizing: 'content' }}
            background="transparent"
            fontSize="14px"
            color="neutral.primary"
            padding="4px"
            width="100%"
            _placeholder={{ color: 'neutral.tertiary' }}
            _readOnly={{
              cursor: 'not-allowed',
              opacity: 0.6,
              caretColor: 'transparent',
            }}
          />
          <ChakraFlex
            alignItems="center"
            justifyContent="flex-end"
            paddingTop="4px"
          >
            <ChakraBox data-testid="mai-chat-composer__send">
              <FireflyIconButton
                id="mai-chat-composer__send"
                ariaLabel="Send message"
                icon={FAIconName.arrowUpRegular}
                isDisabled={isEmptyInput || isDisabled}
                onClick={() => submitTextarea('Send Button')}
                size="small"
                variant="primary"
              />
            </ChakraBox>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
      <Text
        data-testid="mai-chat-composer__disclaimer"
        color="neutral.secondary"
        marginTop="14px"
        textAlign="center"
        variant="caption"
      >
        {MAI_CHAT_DISCLAIMER}
      </Text>
    </ChakraBox>
  );
};

export default MaiChatComposer;
